import React from 'react';
import {
  TripMatchStatus, TripMessageState, SalesStage,
} from '@marageti/z4-sdk/lib/travel';
import {
  Box,
  Stack,
  SvgIcon, Tooltip, Typography,
  SxProps,
} from '@mui/material';
import ReactCountryFlag from 'react-country-flag';
import {
  OutlinedIcons, CustomIcons, formatPhoneNumber,
} from '@marageti/z4-lib';
import { CountryCodeMap } from '@marageti/z4-sdk/lib/standards';
import { calculateRemainingTime } from '../../../utils/helpers';
import { ReactComponent as SalesStage1 } from '../../../assets/svg/sales-01.svg';
import { ReactComponent as SalesStage2 } from '../../../assets/svg/sales-02.svg';
import { ReactComponent as SalesStage3 } from '../../../assets/svg/sales-03.svg';

export { default as PecvIndicator } from './pecv-indicator-container';

const {
  OutlinedPhoneIcon,
  OutlinedPhoneMissedCallIcon,
  OutlinedBanIcon,
} = OutlinedIcons;

const {
  MessageNoNewIcon,
  MessageErrorIcon,
  MessageTimerOrangeIcon,
  MessageNewIcon,
  MessageNewOrangeIcon,
  SalesStage0Icon,
  SalesStage1Icon,
  SalesStage2Icon,
  SalesStage3Icon,
  SalesStage4Icon,
} = CustomIcons;

export const PhoneIndicator = ({
  primaryTravelerPhone,
}: {
  primaryTravelerPhone: any
}) => {
  const {
    prefix,
    number,
  } = primaryTravelerPhone || {};
  const phoneNumber = formatPhoneNumber(prefix, number);

  return (
    <Tooltip title={phoneNumber ? 'Phone Number Provided' : 'Missing Phone Number'}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <SvgIcon
          color={phoneNumber ? 'primary' : 'disabled'}
          component={phoneNumber ? OutlinedPhoneIcon : OutlinedPhoneMissedCallIcon}
        />
      </div>
    </Tooltip>
  );
};

const NotificationDot = () => (
  <span
    style={{
      height: '8px',
      width: '8px',
      backgroundColor: '#0089FC',
      borderRadius: '50%',
      display: 'inline-block',
      verticalAlign: 'middle',
    }}
  />
);

export const MinimalChatIndicator = ({
  agentAssignedTo,
  teamMemberAssignedTo,
  tripMessageState,
}: {
  agentAssignedTo?: string,
  teamMemberAssignedTo: string,
  tripMessageState: TripMessageState,
}) => {
  const assignedToMessageState = tripMessageState.find((m) => (
    m.personId !== ''
    && m.hasNew
    && (m.personId === agentAssignedTo || m.personId === teamMemberAssignedTo)
  ));
  const hasNewMessage = assignedToMessageState?.hasNew || false;
  return hasNewMessage ? <NotificationDot /> : null;
};

export const ChatIndicator = ({
  assignedTo,
  createdAt,
  isAgentContext = false,
  tripMatchStatus,
  tripMessageState,
  sx,
}:{
  assignedTo: string,
  createdAt: string,
  isAgentContext?: boolean,
  tripMatchStatus?: TripMatchStatus | undefined,
  tripMessageState: TripMessageState,
  sx?: SxProps,
}) => {
  const assignedToMessageState = tripMessageState.find((m) => m.personId !== '' && m.personId === assignedTo);
  const hasNewMessage = assignedToMessageState?.hasNew ?? false;
  const isStatusAcceptedOrPending = tripMatchStatus === TripMatchStatus.MatchAccepted || tripMatchStatus === TripMatchStatus.MatchPending;
  const timeRemaining = calculateRemainingTime(createdAt);

  if (isStatusAcceptedOrPending) {
    if (timeRemaining <= 0) {
      return (
        <Tooltip title="Overdue">
          <span>
            <MessageErrorIcon />
          </span>
        </Tooltip>
      );
    }

    return (
      <Tooltip title={timeRemaining > 24 ? 'Traveler awaiting first message' : `${timeRemaining}h left to send the first message`}>
        <span>
          <MessageTimerOrangeIcon />
        </span>
      </Tooltip>
    );
  }

  let MessageIcon;
  if (hasNewMessage) {
    MessageIcon = isAgentContext ? MessageNewOrangeIcon : MessageNewIcon;
  } else {
    MessageIcon = MessageNoNewIcon;
  }

  let tooltipText;
  if (isAgentContext) {
    tooltipText = hasNewMessage
      ? 'Partner has a new message from traveler'
      : 'Partner has no unread messages';
  } else {
    tooltipText = hasNewMessage
      ? 'New message received'
      : 'No new messages';
  }

  return (
    <Tooltip title={tooltipText}>
      <Box component="span" sx={sx}>
        <MessageIcon color={hasNewMessage ? 'primary' : 'disabled'} />
      </Box>
    </Tooltip>
  );
};

const salesStageIcons = {
  [SalesStage.NeedsFollowUp]: { icon: <SalesStage1 />, tooltip: 'Needs Follow Up' }, // TODO: Deprecate
  [SalesStage.NeedsProposal]: { icon: <SalesStage2 />, tooltip: 'Needs Proposal' }, // TODO: Deprecate
  [SalesStage.ProposalSent]: { icon: <SalesStage3 />, tooltip: 'Proposal Sent' }, // TODO: Deprecate
  [SalesStage.FirstMessageSent]: { icon: <SalesStage1Icon />, tooltip: 'Sales Stage: First Message' },
  [SalesStage.Consultation]: { icon: <SalesStage2Icon />, tooltip: 'Sales Stage: Consultation' },
  [SalesStage.ItinerarySent]: { icon: <SalesStage3Icon />, tooltip: 'Sales Stage: Proposal Sent' },
  [SalesStage.ItineraryInReview]: { icon: <SalesStage4Icon />, tooltip: 'Sales Stage: Itinerary Review' },
};

export const SalesStageIndicator = ({ salesStage }: {
  salesStage: SalesStage,
}) => {
  const { icon, tooltip } = salesStageIcons[salesStage as keyof typeof salesStageIcons] || { icon: <SalesStage0Icon />, tooltip: 'Sales Stage: Undefined' };
  return (
    <Tooltip title={tooltip}>
      <span>
        {icon}
      </span>
    </Tooltip>
  );
};

export const RecaptchaScoreIndicator = ({ recaptchaScore }: { recaptchaScore: number }) => {
  const recaptchaLevels: Array<{
    min: number;
    icon: React.ComponentType<{ fontSize: 'small' }>;
    color: string;
    label: number;
    message: string;
    bgcolor: string;
  }> = [
    {
      min: 0.9,
      icon: OutlinedIcons.OutlinedEmojiHappyIcon,
      color: 'success.main',
      label: 9,
      message: "ReCaptcha Score (0.9): It's a human!",
      bgcolor: 'success.light',
    },
    {
      min: 0.7,
      icon: OutlinedIcons.OutlinedEmojiHappyIcon,
      color: 'success.main',
      label: 7,
      message: 'ReCaptcha Score (0.7): Likely a human!',
      bgcolor: 'success.light',
    },
    {
      min: 0.5,
      icon: OutlinedIcons.OutlinedChipIcon,
      color: 'error.main',
      label: 5,
      message: 'ReCaptcha Score (0.5): Probably a bot!',
      bgcolor: 'error.light',
    },
    {
      min: 0.3,
      icon: OutlinedIcons.OutlinedChipIcon,
      color: 'error.main',
      label: 3,
      message: 'ReCaptcha Score (0.3): Likely a bot!',
      bgcolor: 'error.light',
    },
    {
      min: 0,
      icon: OutlinedIcons.OutlinedChipIcon,
      color: 'error.main',
      label: 1,
      message: "ReCaptcha Score (0.1): It's a bot!",
      bgcolor: 'error.light',
    },
  ];

  const neutralIndicator = {
    icon: OutlinedIcons.OutlinedQuestionMarkCircleIcon,
    color: 'action.disabled',
    label: '-',
    message: 'ReCaptcha Score: Not Available',
    bgcolor: 'action.disabledBackground',
  };

  const recaptchaIndicator = recaptchaScore === null
    ? neutralIndicator
    : recaptchaLevels.find((level) => recaptchaScore >= level.min) ?? neutralIndicator;

  return (
    <Tooltip title={recaptchaIndicator.message}>
      <Box
        sx={{
          paddingX: 1,
          paddingY: 0.5,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          bgcolor: recaptchaIndicator.bgcolor,
          borderRadius: '6.25rem',
        }}
      >
        <Stack direction="row" spacing={0.5} alignItems="center">
          <SvgIcon sx={{ color: recaptchaIndicator.color }} fontSize="small" component={recaptchaIndicator.icon} />
          <Typography variant="captionBold" color="primary">{recaptchaIndicator.label}</Typography>
        </Stack>
      </Box>
    </Tooltip>
  );
};

export const IPIndicator = ({ countryCodes = {}, submittedFrom }: { countryCodes: CountryCodeMap, submittedFrom: string | undefined }) => {
  const title = `Proxy IP: ${submittedFrom ? countryCodes[submittedFrom] : 'Not Available'}`;
  return (
    <Tooltip title={title}>
      <Box>
        { submittedFrom && countryCodes[submittedFrom] && (
          <ReactCountryFlag style={{ width: '1.5rem', height: '1.5rem' }} countryCode={submittedFrom!} svg />
        )}
        {!submittedFrom && (
          <SvgIcon color="disabled" component={OutlinedBanIcon} />
        )}
      </Box>
    </Tooltip>
  );
};
